/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

export interface IssuesStateInterface {
  currentIssue: IssueInterface | undefined;
  currentContributors: ContributionListInterface | undefined;
  searchTerm?: string;
  [key: string]: IssueInterface |
      ContributionListInterface |
      IssueListInterface |
      string | undefined;
}

export interface IssueListInterface {
  issues: IssueInterface[];
  pagination: PaginationInterface;
  totalItems: number;
}

interface IssueDetailsInterface {
  idString: string;
  description: string;
  reproducedByOther?: number;
  stepsToReproduce: string;
  frequency?: number;
  platform?: PcPlatformInterface | XboxPlatformInterface | PsPlatformInterface;
  mediaFiles?: FileInterface[];
  timeSeen: number;
}

export interface IssueInterface {
  id: number;
  title: string;
  status: string;
  issueDetails?: IssueDetailsInterface;
  tags?: string[];
  category: IssueCategoryInterface;
  voteWeight?: number;
  vote?: {
    id: string;
    type: string;
  } | null;
  dateCreated?: string;
  dateEdited?: string;
  canVote?: boolean;
  game: string;
  platforms: string[];
  isOwner?: boolean;
  reasonOfResolution?: string;
  version?: number;
}

export interface IssueCategoryInterface {
  id: string;
  type: string;
  gameName: string;
}

export interface IssueUpdateConfigInterface {
  issueId: number;
  issueDetailsIdString: string;
  titleData: {title: string};
  detailsData: {
    description: string;
    how_to_reproduce: string;
    time_seen: number;
  };
  captcha_token?: string;
}

export interface IssuesResultsInterface {
  issues: IssueInterface[];
  stateName: string;
  totalIssues: number;
  pagination: PaginationInterface;
}

export interface PaginationInterface {
  id: string;
  type: string;
  next?: string;
  previous?: string;
  first?: string;
  last?: string;
}

export interface IssueFilterInterface {
  orderByDate?: string;
  orderByVote?: string;
  status?: string | string[];
  platform?: string | string[];
  itemsPerPage?: number;
  searchTerm?: string;
  gameId?: number | null;
  category?: string;
  customerId?: string;
  version?: any;
}

export interface IssuesConfigInterface {
    filters: IssueFilterInterface;
    stateName: string;
    path?: string;
    showIssueSummary?: boolean;
}

export interface AbstractPlatformInterface {
  id: number;
  type: string;
  region: string;
  internetProvider: string;
  internetSpeed: string;
  additionalTechnicalInfo: string;
}

export interface XboxPlatformInterface extends AbstractPlatformInterface {
  platformType: string;
}

export interface PsPlatformInterface extends AbstractPlatformInterface {
  platformType: string;
}

export interface PcPlatformInterface extends AbstractPlatformInterface {
  os: string;
  processor: string;
  memory: string;
  directXVersion: string;
  graphicCardChipType: string;
  loadedVia: string;
}

export interface FileInterface {
  id: number;
  mimeType: string;
  name: string;
  presignedUrl: string;
}

export interface S3UploadInterface {
  preSignedUrl: string;
  file: File;
}

export interface ContributorInterface {
  username?: string;
  isAdmin?: boolean;
  description?: string;
  dateCreated?: string;
  mediaFiles?: MediaInterface;
  canReproduce: boolean;
  howToReproduce: string;
  timeSeen: number;
}

export interface MediaInterface {
  images: MediaImagesInterface[];
  links: string[];
}

export interface MediaImagesInterface {
 id: number;
 name: string;
 slug: string;
}

export interface VoteInterface {
  issueId: number;
  stateName: string;
  vote: {id: string, type?: string} | null;
}

export interface VoteConfigInterface extends VoteInterface {
  voteData: {issue: string};
}

export interface VoteDeleteConfigInterface extends VoteInterface {
  voteId: string;
}

export interface ContributionInterface {
  description: string;
  can_reproduce?: boolean;
  how_to_reproduce?: string;
  log_link?: string;
  time_seen?: number;
  creator_type: string; // contributor
  issue: string; // issue id
  platform?: string;
  captcha_token?: string;
}

export interface ContributionConfigInterface {
    issueId: number;
    path?: string;
    itemsPerPage?: number;
}

export interface ContributionListInterface {
    contributors: ContributorInterface[];
    pagination: PaginationInterface;
    totalItems: number;
}

export interface MediaCreateInterface {
    name: string;
    mime_type: string;
    size: number;
    path: string;
}

export interface EvidenceCreateInterface {
    issueDetailsId: number;
    fileIdsData: string[];
}

export enum IssueTitleType {
  LIST = 0,
  FORM = 1,
  TITLE = 2,
}

export enum Contributions {
  ITEMS_PER_PAGE = 10,
}
